import { useCallback, useEffect, type FC } from "react";

import { AutocompleteTypeEnum, useFetchEntitiesAutocomplete } from "@ds/hooks/use-fetch-entities";
import { useIsFirstTimeTrue } from "@ds/hooks/use-is-first-time-true";
import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { AutoComplete, type SingleAutoCompleteProps } from "@ds/components/forms/autocomplete";
import { SpinnerIcon } from "@ds/components/loaders/loaders";

import { selectLocationById, selectLocationsIsLoading } from "../redux/selectors";
import { locationsActions } from "../redux/slice";
import { isLocation } from "../utils/model";

export const LocationsAutoComplete: FC<Omit<SingleAutoCompleteProps<Locality>, "isLoading">> = ({
  customRef,
  inputRef,
  value,
  filterSuggestionsPredicate,
  dropdownMode,
  autoFocus,
  onBlur,
  onChange,
}) => {
  const [
    suggestions,
    onCompleteHandler,
    {
      state: { status: remoteStatus },
    },
  ] = useFetchEntitiesAutocomplete<Locality>(AutocompleteTypeEnum.Locations, {
    filterPredicate: filterSuggestionsPredicate,
  });

  return (
    <AutoComplete
      field="name"
      placeholder="Enter location name"
      customRef={customRef}
      inputRef={inputRef}
      value={value?.id ? value : null}
      suggestions={suggestions}
      dropdownMode={dropdownMode}
      autoFocus={autoFocus}
      isLoading={remoteStatus === "loading"}
      onBlur={onBlur}
      onChange={onChange}
      onComplete={onCompleteHandler}
    />
  );
};

export const LocationsAutocompleteRaw: FC<
  Omit<SingleAutoCompleteProps<Locality>, "value" | "onChange" | "isLoading"> & {
    value?: number | null;
    onChange: (id: number | null, location: Locality | null) => void;
  }
> = ({ value, onChange, ...restProps }) => {
  const isLoading = useRootSelector(selectLocationsIsLoading);
  const currentValue = useRootSelector(state =>
    isLocation(value) ? value : selectLocationById(state, Number(value || 0)),
  );

  const [isFirstLoading] = useIsFirstTimeTrue(isLoading);

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (!isLocation(value) && Number(value)) {
      dispatch(locationsActions.fetchLocation(Number(value), { shouldDisableSagaThrottling: true }));
    }
  }, [dispatch, value]);

  const onChangeHandler = useCallback(
    (location: Locality) => onChange(location?.id ?? null, location ?? null),
    [onChange],
  );

  return isFirstLoading ? (
    <SpinnerIcon />
  ) : (
    <LocationsAutoComplete value={currentValue} onChange={onChangeHandler} {...restProps} />
  );
};
